import { DocumentNode } from '@apollo/client';
import { useCallback, useState } from 'react';

import { OrderByArgs, PageInfo, PaginationArgs } from '@/@generated/codegen/API';
import { useApolloClientContext } from '@/components/CustomApolloProvider/CustomApolloProvider';

export const useEntityFetchAll = <
  QueryResult,
  QueryVariables extends {
    orderByArgs?: OrderByArgs | null;
    paginationArgs?: PaginationArgs | null;
    where?: any;
  },
  Twhere,
  TFragment,
>(
  where: Twhere,
  queryDoc: DocumentNode,
  extractFragmentList: (data: QueryResult) => TFragment[],
  extractPageInfo: (data: QueryResult) => PageInfo,
  onResultCallback: (result: TFragment[]) => void
) => {
  const { apolloClient } = useApolloClientContext();
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    let hasMore = true;
    let cursor: string | undefined = undefined;
    let allItems: TFragment[] = [];

    try {
      while (hasMore) {
        const { data } = await apolloClient.query<QueryResult, QueryVariables>({
          query: queryDoc,
          variables: {
            orderByArgs: {
              orderByField: 'createdAt',
              direction: 'desc',
            },
            paginationArgs: {
              first: 100,
              after: cursor,
            },
            where,
          } as QueryVariables,
          fetchPolicy: 'network-only',
        });

        const items = extractFragmentList(data);
        allItems = allItems.concat(items);

        const pageInfo = extractPageInfo(data);
        cursor = pageInfo.endCursor ?? undefined;
        hasMore = pageInfo.hasNextPage;
      }

      onResultCallback(allItems);
    } finally {
      setLoading(false);
    }
  }, [where, queryDoc, onResultCallback]);

  return {
    fetchAll,
    loading,
  };
};
